import { useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { KeyRound, ShieldCheck, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getMyProfile } from "@/lib/api/portal.functions";
import { recordAuthSecurityEvent } from "@/lib/api/security-alerts.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/_authenticated/portal/admin/security")({
  head: () => ({
    meta: [
      { title: "Admin security — rotate password | Nkadimeng Danny Legal & Compliance" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminSecurityPage,
});

function AdminSecurityPage() {
  const fetchProfile = useServerFn(getMyProfile);
  const { data, isLoading } = useQuery({
    queryKey: ["my-profile"],
    queryFn: () => fetchProfile(),
  });

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  if (!data?.isAdmin) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
        This area is restricted to the practice owner.
      </div>
    );
  }

  const email = data?.profile?.email ?? "";

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (newPassword.length < 10) {
      toast.error("New password must be at least 10 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match.");
      return;
    }
    if (newPassword === currentPassword) {
      toast.error("New password must differ from current password.");
      return;
    }

    setSaving(true);
    try {
      // Re-verify current password (acts as step-up auth)
      const { error: signInErr } = await supabase.auth.signInWithPassword({
        email,
        password: currentPassword,
      });
      if (signInErr) throw new Error("Current password is incorrect.");

      const { error: updateErr } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (updateErr) throw updateErr;

      toast.success("Password rotated", {
        description: "Use your new password next time you sign in.",
      });
      void recordAuthSecurityEvent({
        data: { eventType: "admin_password_changed", email },
      }).catch(() => {});
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      toast.error("Could not update password", {
        description: err?.message ?? String(err),
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Admin console</p>
        <h2 className="mt-1 flex items-center gap-2 font-display text-2xl text-navy">
          <ShieldCheck className="h-5 w-5" /> Security &amp; credentials
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Rotate the password for <strong>{email}</strong>. Step-up
          verification is required: enter your current password to confirm
          it&rsquo;s really you.
        </p>
      </header>

      <form
        onSubmit={onSubmit}
        className="max-w-md space-y-4 rounded-2xl border border-border bg-card p-6 shadow-soft"
      >
        <div>
          <Label htmlFor="currentPassword">Current password</Label>
          <Input
            id="currentPassword"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
        </div>
        <div>
          <Label htmlFor="newPassword">New password</Label>
          <Input
            id="newPassword"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={10}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Use at least 10 characters with a mix of letters, numbers &amp; symbols.
          </p>
        </div>
        <div>
          <Label htmlFor="confirmPassword">Confirm new password</Label>
          <Input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={10}
          />
        </div>
        <Button type="submit" disabled={saving} className="w-full bg-navy hover:bg-navy/90">
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Rotating…
            </>
          ) : (
            <>
              <KeyRound className="mr-2 h-4 w-4" /> Rotate password
            </>
          )}
        </Button>
      </form>

      <div className="max-w-md rounded-md border border-border bg-muted/30 p-4 text-xs text-muted-foreground">
        <p>
          For LPC &amp; POPIA hygiene: rotate this password at least every 90
          days, never share it, and never reuse it on other services.
        </p>
      </div>
    </div>
  );
}
